import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AppSelect from "@/components/Core/AppSelect";
import { FormLabel } from "@/components/Core/AppText/AppText";
import ButtonNew from "@/components/Core/Button/ButtonNew";
import DayPickerNew from "@/components/Core/DayPicker/DayPickerNew";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ABSENCE_REASONS } from "@/configs/absenceReasons.config";
import { DATE_TIME_FORMAT_TYPES } from "@/configs/constants/DateTimeFormatTypes";
import { useModalStore } from "@/hooks/modal.hooks";
import { CreateAbsencesRequest } from "@/models/Consultant";
import { KeyValue } from "@/models/KeyValue";
import {
	useConsultantAbsence,
	useCreateAbsence,
	useUpdateAbsence,
} from "@/networking/consultant.hooks";
import { contactDateAndTime } from "@/utils/date.time.utils";

interface AbsenceModalProps {
	consultantId: string;
	absenceId?: string;
	title?: string;
	selectedDate?: string;
}

/**
 * Register or edit a consultant absence. Rendered globally by LoggedInLayout
 * and opened from anywhere via `useModalStore.toggleAbsenceModal` (consultant
 * context menu, absence tab, shift planner). Editing can only move the END of
 * the absence — the backend update API takes nothing else — so start and
 * reason render disabled then.
 */
const AbsenceModal: React.FC<AbsenceModalProps> = ({
	consultantId,
	absenceId,
	title,
	selectedDate,
}) => {
	const toggleModal = useModalStore((state) => state.toggleAbsenceModal);
	const { absences } = useConsultantAbsence({ consultantId });
	const { mutateAsync: updateAbsence, isLoading: isUpdating } = useUpdateAbsence();
	const { mutateAsync: createAbsence, isLoading: isCreating } = useCreateAbsence();
	const isSaving = isUpdating || isCreating;

	const { t } = useTranslation();

	const [startDate, setStartDate] = useState<Date>(
		selectedDate ? dayjs(selectedDate).toDate() : new Date()
	);
	const [startTime, setStartTime] = useState<string>("06:00");
	const [endTime, setEndTime] = useState<string>("23:59");

	const [endDate, setEndDate] = useState<Date>(
		selectedDate ? dayjs(selectedDate).toDate() : new Date()
	);
	const [reason, setReason] = useState<string>(String(ABSENCE_REASONS[0].key));

	useEffect(() => {
		if (absenceId && absences) {
			const absence = absences.find((absence) => absence.id === absenceId);

			if (absence) {
				setStartDate(new Date(absence.startDate));
				setEndDate(new Date(absence.endDate));
				setStartTime(dayjs(absence.startDate).format(DATE_TIME_FORMAT_TYPES.TIME));
				setEndTime(dayjs(absence.endDate).format(DATE_TIME_FORMAT_TYPES.TIME));
				if (absence.type) setReason(absence.type);
			}
		}
	}, [absences, absenceId]);

	// Moving the start past the end drags the end along: the end picker's
	// minDate only limits what can be picked, it does not correct the value
	const handleStartDateChange = (date?: Date) => {
		if (!date) return;
		setStartDate(date);
		if (dayjs(endDate).isBefore(date, "day")) setEndDate(date);
	};

	// A cleared time input has no value to compare, and contactDateAndTime
	// can't build a date without one
	const isEndBeforeStart =
		!!startTime &&
		!!endTime &&
		dayjs(contactDateAndTime(endDate, endTime)).isBefore(
			contactDateAndTime(startDate, startTime)
		);

	const handleSubmit = async () => {
		try {
			if (absenceId) {
				await updateAbsence({
					consultantId,
					absenceId,
					update: { endDate: contactDateAndTime(endDate, endTime) },
				});
			} else {
				const body: CreateAbsencesRequest = {
					startDate: contactDateAndTime(startDate, startTime),
					endDate: contactDateAndTime(endDate, endTime),
					type: reason,
				};
				await createAbsence({ consultantId, body });
			}
			toggleModal();
		} catch {
			// The mutation hook already toasts the API error — keep the dialog
			// open so the dates can be corrected and retried
		}
	};

	return (
		<Dialog open onOpenChange={(open) => !open && toggleModal()}>
			<DialogContent className="sm:max-w-[560px]">
				<DialogHeader>
					<DialogTitle>{absenceId ? t("EDIT_ABSENCE") : t("REGISTER_ABSENCE")}</DialogTitle>
					{title && <DialogDescription>{title}</DialogDescription>}
				</DialogHeader>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col">
						<FormLabel>{t("FROM")}</FormLabel>
						<div className="flex gap-2">
							<DayPickerNew
								value={startDate}
								onChange={handleStartDateChange}
								disabled={!!absenceId}
								containerClassName="min-w-0 flex-1"
								triggerClassName="h-9"
								popoverClassName="z-[1300]"
							/>
							<Input
								type="time"
								value={startTime}
								disabled={!!absenceId}
								onChange={(e) => setStartTime(e.target.value)}
								className="w-24 shrink-0"
							/>
						</div>
					</div>

					<div className="flex flex-col">
						<FormLabel>{t("TO")}</FormLabel>
						<div className="flex gap-2">
							<DayPickerNew
								value={endDate}
								minDate={startDate}
								onChange={(date) => date && setEndDate(date)}
								containerClassName="min-w-0 flex-1"
								triggerClassName="h-9"
								popoverClassName="z-[1300]"
							/>
							<Input
								type="time"
								value={endTime}
								onChange={(e) => setEndTime(e.target.value)}
								className="w-24 shrink-0"
							/>
						</div>
					</div>
				</div>

				{isEndBeforeStart && (
					<p role="alert" className="m-0 text-sm text-red-600">
						{t("ABSENCE_END_BEFORE_START")}
					</p>
				)}

				<AppSelect<KeyValue>
					label="REASON"
					options={ABSENCE_REASONS}
					value={reason}
					onChange={setReason}
					disabled={!!absenceId}
					getOptionLabel={(option) => option.value}
					getOptionValue={(option) => String(option.key)}
					contentClassName="z-[1300]"
					margin="0"
				/>

				<DialogFooter className="mt-2">
					<ButtonNew variant="neutral" onClick={() => toggleModal()}>
						{t("CANCEL")}
					</ButtonNew>
					<ButtonNew onClick={handleSubmit} disabled={isSaving || isEndBeforeStart}>
						{absenceId ? t("EDIT_ABSENCE") : t("REGISTER_ABSENCE")}
					</ButtonNew>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default AbsenceModal;
